import React, { useState } from "react"
import PropTypes from "prop-types"
import { TextField } from "@mui/material"

function clamp(n, min, max) {
  if (typeof min === "number" && n < min) return min
  if (typeof max === "number" && n > max) return max
  return n
}

/**
 * Number input that allows an empty field while focused (e.g. clearing a leading 0).
 * On blur, commits a parsed number or emptyFallback; optional min/max clamp after parse.
 */
const InvoiceNumericTextField = ({
  value,
  onCommit,
  emptyFallback,
  min,
  max,
  readOnly,
  inputProps = {},
  ...rest
}) => {
  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState("")

  if (readOnly) {
    return (
      <TextField
        {...rest}
        type="number"
        value={value ?? ""}
        onChange={e => onCommit(Number(e.target.value))}
        inputProps={inputProps}
      />
    )
  }

  const displayValue = focused
    ? draft
    : value === undefined || value === null || Number.isNaN(value)
      ? ""
      : value

  const handleFocus = () => {
    setFocused(true)
    setDraft(
      value === undefined || value === null || Number.isNaN(value)
        ? ""
        : String(value),
    )
  }

  const handleChange = e => {
    const raw = e.target.value
    setDraft(raw)
    if (raw === "" || raw === "-") {
      onCommit(null)
      return
    }
    const parsed = Number(raw)
    if (Number.isNaN(parsed)) return
    onCommit(parsed)
  }

  const handleBlur = () => {
    setFocused(false)
    const t = draft.trim()
    let next = emptyFallback
    if (t !== "") {
      const parsed = Number(t)
      if (!Number.isNaN(parsed)) next = clamp(parsed, min, max)
    }
    onCommit(next)
    setDraft("")
  }

  return (
    <TextField
      {...rest}
      type="number"
      value={displayValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      inputProps={inputProps}
    />
  )
}

InvoiceNumericTextField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  onCommit: PropTypes.func.isRequired,
  emptyFallback: PropTypes.number.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  readOnly: PropTypes.bool,
  inputProps: PropTypes.object,
}

export default InvoiceNumericTextField
